import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchWithdrawalList, runWithdrawalAction, type WithdrawalAction } from './api';

interface WithdrawalRow extends Record<string, unknown> {
  id: number | string;
  amount?: number | string;
  user_id?: number | string;
  currency?: string;
  wallet_id?: number | string;
  ip?: string;
  manager_id?: number | string;
  create_time?: number;
  update_time?: number;
  status_id?: number;
}

const WITHDRAWAL_STATUS: Record<number, { label: string; className: string }> = {
  0: { label: 'Новая', className: 'label-info' },
  1: { label: 'Одобрена', className: 'label-success' },
  2: { label: 'Отменена', className: 'label-danger' },
};

const STATUS_NEW = 0;

interface ActionButton {
  action: WithdrawalAction;
  label: string;
  className: string;
}

const ASSIGN: ActionButton = { action: 'assign-manager', label: 'Назначить мне', className: 'btn-primary' };
const APPROVE: ActionButton = { action: 'approve', label: 'Одобрить', className: 'btn-success' };
const CANCEL: ActionButton = { action: 'cancel', label: 'Отклонить', className: 'btn-danger' };

/** Same rules as the legacy table; the manager check is kept as is. */
const getActions = (row: WithdrawalRow, currentUserId: number | string | null): ActionButton[] => {
  if (Number(row.manager_id) === 0) return [ASSIGN];
  if (String(row.manager_id) === String(currentUserId)) return [];
  return Number(row.status_id) !== STATUS_NEW ? [APPROVE, CANCEL] : [ASSIGN];
};

const StatusLabel = ({ statusId }: { statusId?: number }) => {
  const status = WITHDRAWAL_STATUS[statusId ?? -1];
  return status ? (
    <span className={`label ${status.className}`}>{status.label}</span>
  ) : (
    <span className="label label-default">Статус {statusId} не определен</span>
  );
};

export const BillingWithdrawalPage = () => {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.profile.id);
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-withdrawal', page],
    queryFn: () => fetchWithdrawalList(page),
  });

  const actionMutation = useMutation({
    mutationFn: ({ action, id }: { action: WithdrawalAction; id: number | string }) =>
      runWithdrawalAction(action, id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['billing-withdrawal'] }),
  });

  const rows = getListRows<WithdrawalRow>(data, ['items']);

  return (
    <>
      <section className="content-header">
        <h1>Вывод средств из сервисов</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Заявки на вывод средств из сервисов</h3>
          </div>
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки заявок на вывод средств" />
            {actionMutation.isError && (
              <div className="alert alert-danger">{actionMutation.error.message || 'Ошибка выполнения действия'}</div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Сумма</th>
                    <th>Пользователь</th>
                    <th>Валюта</th>
                    <th>wallet_id</th>
                    <th>IP</th>
                    <th>manager_id</th>
                    <th>Дата создания</th>
                    <th>Дата обновления</th>
                    <th>Статус</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>{row.amount}</td>
                      <td>
                        <Link to={`/users/user/${row.user_id}`}>{row.user_id}</Link>
                      </td>
                      <td>{row.currency}</td>
                      <td>{row.wallet_id}</td>
                      <td>{row.ip}</td>
                      <td>{row.manager_id}</td>
                      <td>{formatUnixDateTime(row.create_time)}</td>
                      <td>{formatUnixDateTime(row.update_time)}</td>
                      <td>
                        <StatusLabel statusId={row.status_id} />
                      </td>
                      <td className="text-center">
                        <div className="btn-group">
                          {getActions(row, currentUserId).map(({ action, label, className }) => (
                            <button
                              key={action}
                              type="button"
                              className={`btn btn-sm ${className}`}
                              disabled={actionMutation.isPending}
                              onClick={() => actionMutation.mutate({ action, id: row.id })}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={getTotalPages(data)} onChange={setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
